import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import type {
  CreateServiceRequestItemInput,
  CreateServiceRequestResponse,
  RequestType,
} from '@hotel-voice/shared';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { requestNotificationPermission } from '@/contexts/RequestsContext';
import { newIdempotencyKey } from '@/lib/ids';
import { createServiceRequest } from '@/services/firebase/callables';
import { toUserMessage } from '@/utils/errors';

export interface SubmitRequestState {
  submitting: boolean;
  result: CreateServiceRequestResponse | null;
  errorMessage: string | null;
  submit: (input: { items: CreateServiceRequestItemInput[]; note?: string }) => Promise<boolean>;
  /** Starts a fresh submission (new idempotency key) — after a success, for "order again". */
  reset: () => void;
}

/**
 * Files one request of `type` for the signed-in guest, with the two guards
 * every submit button needs:
 *
 *  - **No double submit.** `submitting` is held in a ref as well as state, so
 *    a second tap that lands before React re-renders is ignored too.
 *  - **No duplicate order.** One idempotency key per attempt-series, minted
 *    when the page mounts and kept until a submission succeeds. A retry after
 *    a timeout sends the same key and the server returns the request it
 *    already created rather than a second one.
 *
 * Room, booking and guest identity are never passed in: the server pins them
 * to the session (`createServiceRequest` → `resolveGuestContext`).
 */
export function useSubmitRequest(type: RequestType): SubmitRequestState {
  const { location } = useReadyGuest();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateServiceRequestResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inFlight = useRef(false);
  const keyRef = useRef(newIdempotencyKey());

  const submit = useCallback(
    async ({ items, note }: { items: CreateServiceRequestItemInput[]; note?: string }) => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setSubmitting(true);
      setErrorMessage(null);
      try {
        const response = await createServiceRequest({
          hotelId: location.hotelId,
          roomId: location.roomId,
          type,
          items,
          ...(note?.trim() ? { note: note.trim() } : {}),
          idempotencyKey: keyRef.current,
        });
        setResult(response);
        // Now there is something to be notified about. Asked after the tap,
        // never on page load, so the prompt has a reason the guest can see.
        void requestNotificationPermission();
        return true;
      } catch (error) {
        const message = toUserMessage(error);
        setErrorMessage(message);
        toast.error(message);
        return false;
      } finally {
        inFlight.current = false;
        setSubmitting(false);
      }
    },
    [location.hotelId, location.roomId, type],
  );

  const reset = useCallback(() => {
    keyRef.current = newIdempotencyKey();
    setResult(null);
    setErrorMessage(null);
  }, []);

  return { submitting, result, errorMessage, submit, reset };
}
